import { format } from 'date-fns';
import {
  HeartIcon,
  ChartBarIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { BreedingList } from '../../services/reports';

interface BreedingListReportProps {
  data: BreedingList | null;
  loading: boolean;
  error: string | null;
  onReload: () => void;
}

export default function BreedingListReport({
  data,
  loading,
  error,
  onReload,
}: BreedingListReportProps) {
  if (loading) {
    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto"></div>
            <p className="mt-2 text-sm text-slate-600">Loading breeding list...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="text-center">
            <p className="text-red-600">{error}</p>
            <button
              onClick={onReload}
              className="mt-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-cyan-700 bg-cyan-100 hover:bg-indigo-200"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div>
      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-6">
        <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6">
          <dt>
            <div className="absolute rounded-md bg-pink-500 p-3">
              <HeartIcon className="h-6 w-6 text-white" aria-hidden="true" />
            </div>
            <p className="ml-16 truncate text-sm font-medium text-slate-500">Active Pairs</p>
          </dt>
          <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
            <p className="text-2xl font-semibold text-slate-900">{data.activePairs}</p>
            <p className="ml-2 text-sm text-slate-500">of {data.totalPairs}</p>
          </dd>
        </div>

        <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6">
          <dt>
            <div className="absolute rounded-md bg-green-500 p-3">
              <ChartBarIcon className="h-6 w-6 text-white" aria-hidden="true" />
            </div>
            <p className="ml-16 truncate text-sm font-medium text-slate-500">Success Rate</p>
          </dt>
          <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
            <p className="text-2xl font-semibold text-slate-900">
              {data.breedingPairs && data.breedingPairs.length > 0
                ? Math.round(data.breedingPairs.reduce((acc, pair) => acc + pair.successRate, 0) / data.breedingPairs.length) : 0}%
            </p>
          </dd>
        </div>

        <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6">
          <dt>
            <div className="absolute rounded-md bg-gradient-to-br from-cyan-500 to-violet-500 p-3">
              <CalendarIcon className="h-6 w-6 text-white" aria-hidden="true" />
            </div>
            <p className="ml-16 truncate text-sm font-medium text-slate-500">Total Clutches</p>
          </dt>
          <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
            <p className="text-2xl font-semibold text-slate-900">
              {data.breedingPairs ? data.breedingPairs.reduce((acc, pair) => acc + pair.totalClutches, 0) : 0}
            </p>
          </dd>
        </div>
      </div>

      {/* Breeding Pairs Table */}
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Breeding Pairs</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Pair Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Male
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Female
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Paired Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Clutches
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Success Rate
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(data.breedingPairs || []).map((pair: any) => (
                  <tr key={pair.id} className="hover:bg-slate-50/80">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      {pair.pairName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      <div>
                        <div className="font-medium">{pair.male.bandNumber}</div>
                        <div className="text-slate-500 text-xs">
                          {pair.male.species || 'Unknown Species'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      <div>
                        <div className="font-medium">{pair.female.bandNumber}</div>
                        <div className="text-slate-500 text-xs">
                          {pair.female.species || 'Unknown Species'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {format(new Date(pair.pairedDate), 'MMM dd, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        pair.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-800' :
                        pair.status === 'INACTIVE' ? 'bg-violet-100 text-violet-800' :
                        'bg-amber-100 text-amber-800'
                      }`}>
                        {pair.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      <div className="text-sm">
                        <div>{pair.totalClutches} total</div>
                        <div className="text-xs text-slate-500">
                          {pair.totalEggs} eggs, {pair.totalHatched} hatched
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      <div className="flex items-center">
                        <div className="text-sm font-medium">{pair.successRate}%</div>
                        <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-600 h-2 rounded-full"
                            style={{ width: `${Math.min(pair.successRate, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(!data.breedingPairs || data.breedingPairs.length === 0) && (
            <div className="text-center py-8">
              <HeartIcon className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-2 text-sm font-medium text-slate-900">No breeding pairs found</h3>
              <p className="mt-1 text-sm text-slate-500">
                No breeding pairs match the selected date range.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
